import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import * as authApi from '../../Api/authApi';
import { useUserDetails } from '../../hooks/useUser';
import { useCompany } from '../../hooks/useCompany';
import { useEmployees } from '../../hooks/useEmployee';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
	buildPortalUrl,
	buildApexUrl,
	getPortalSubdomain,
	getCookie,
	clearAllClientAuth,
	resolveInitialAuthToken,
	consumeUrlAuthToken,
	consumeLoggedOutQueryParam,
	resolveLogoutAuthUrl,
	requestServerLogout,
	isCompanyUserOnWrongPortalHost,
	isSystemAdminOnPortalHost,
	resolvePostAuthRedirectUrl,
	buildAuthenticatedHostRedirectUrl,
	isClientPortalPath,
} from '../../utils/subdomainUtils';
import { getDashboardPathForRole } from '../../utils/dashboardNavigation';
import {
	touchActivity,
	clearSessionIdleState,
} from '../../utils/sessionIdleStorage';

const AuthenticatedUserInfoContext = createContext();

export const AuthenticatedUserInfoProvider = ({ children }) => {
	const queryClient = useQueryClient();
	const [token, setToken] = useState(() => resolveInitialAuthToken());
	const navigate = useNavigate();
	const location = useLocation();
	const {
		data: user,
		isLoading: userLoading,
		isError: userIsError,
		error: userError,
		refetch: refetchUser,
	} = useUserDetails();
	const [authLoading, setAuthLoading] = useState(true);

	const [companyId, setCompanyId] = useState(null);
	const [userId, setUserId] = useState(null);
	const [userEmail, setUserEmail] = useState(null);
	const [employeeId, setEmployeeId] = useState(null);
	const [userRole, setUserRole] = useState(null);
	const [isCompanyOwner, setIsCompanyOwner] = useState(false);
	const [profileImage, setProfileImage] = useState(null);
	const [userPermissions, setUserPermissions] = useState([]);

	const { data: company } = useCompany(companyId);
	const { data: employees = [], isLoading: employeesLoading } = useEmployees({
		enabled: !!token && !!user && !userIsError,
	});
	const [companyLoading, setCompanyLoading] = useState(true);
	const urlTokenHandledRef = useRef(false);

	useEffect(() => {
		if (urlTokenHandledRef.current) return;

		const urlToken = consumeUrlAuthToken();
		if (!urlToken) return;

		urlTokenHandledRef.current = true;
		setToken(urlToken);
	}, []);

	useEffect(() => {
		if (consumeLoggedOutQueryParam()) {
			setToken(null);
			setUserId(null);
			setUserEmail(null);
			setCompanyId(null);
			setEmployeeId(null);
			setUserRole(null);
			setIsCompanyOwner(false);
			setProfileImage(null);
			setUserPermissions([]);
		}
	}, []);

	useEffect(() => {
		// Skip staff auth on client portal and password-reset pages
		if (
			location.pathname.startsWith('/reset-password') ||
			isClientPortalPath(location.pathname)
		) {
			setAuthLoading(false);
			return;
		}

		if (token) {
			// Small delay to ensure token is properly set in axiosInstance
			setTimeout(() => {
				refetchUser().finally(() => setAuthLoading(false));
			}, 200);
		} else {
			setAuthLoading(false);
		}
	}, [token, refetchUser, location.pathname]);

	useEffect(() => {
		if (
			location.pathname.startsWith('/reset-password') ||
			isClientPortalPath(location.pathname)
		) {
			return;
		}

		if (userLoading) return;

		if (user) {
			const userData = user.data;
			setUserId(userData._id || userData.id);
			setUserRole(userData.role);
			setUserEmail(userData.email);
			setUserPermissions(userData.permissions || []);
			setIsCompanyOwner(Boolean(userData.isCompanyOwner));
			if (userData.company) {
				setCompanyId(userData.company);
				setEmployeeId(userData.employee);
			} else if (userData.role === 'System Admin') {
				// System Admins belong in the System Admin app only
				logoutUser();
			} else {
				logoutUser();
			}
		} else if (!userLoading && userIsError) {
			// Log out on invalid token, deleted user, or suspended account
			const status = userError?.response?.status;
			if (status === 401 || status === 404) {
				logoutUser();
			}
			if (
				status === 403 &&
				userError?.response?.data?.code === 'ACCOUNT_SUSPENDED'
			) {
				logoutUser();
			}
		}
		// eslint-disable-next-line
	}, [user, userLoading, userIsError, location.pathname]);

	// Extract profile image from employees data when available
	useEffect(() => {
		if (employees && employees.length > 0 && employeeId) {
			const currentEmployee = employees.find((emp) => emp._id === employeeId);
			if (currentEmployee && currentEmployee.profileImage) {
				setProfileImage(currentEmployee.profileImage);
			}
		}
	}, [employees, employeeId]);

	// Company info effect - simplified to just store basic company data
	useEffect(() => {
		if (company) {
			setCompanyLoading(false);
		} else if (companyId) {
			setCompanyLoading(true);
		}
	}, [company, companyId]);

	// Keep authenticated users on the correct host for their role.
	useEffect(() => {
		if (authLoading || userLoading || !token || !userRole) return;
		if (
			location.pathname.startsWith('/reset-password') ||
			isClientPortalPath(location.pathname)
		) {
			return;
		}

		const companyData = company?.data?.data || company?.data || company;
		const portalUrlPrefix = companyData?.portalUrlPrefix || null;
		const authToken = token || localStorage.getItem('token') || getCookie('authToken');
		const dashboardPath = getDashboardPathForRole(userRole);
		const isAuthenticated = true;

		const onWrongCompanyHost = isCompanyUserOnWrongPortalHost({
			userRole,
			portalUrlPrefix,
			hostname: window.location.hostname,
			pathname: location.pathname,
			isAuthenticated,
		});
		const onPortalAsSystemAdmin = isSystemAdminOnPortalHost({
			userRole,
			hostname: window.location.hostname,
			isAuthenticated,
		});

		if (!onWrongCompanyHost && !onPortalAsSystemAdmin) return;

		const redirectUrl = buildAuthenticatedHostRedirectUrl({
			userRole,
			portalUrlPrefix,
			pathname: location.pathname,
			search: location.search,
			token: authToken,
			dashboardPath,
		});

		if (redirectUrl) {
			window.location.replace(redirectUrl);
		}
	}, [
		authLoading,
		userLoading,
		token,
		userRole,
		company,
		location.pathname,
		location.search,
	]);

	const completePostAuthNavigation = async (apiToken, portalUrlPrefix) => {
		let attempts = 0;
		const maxAttempts = 30;

		const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

		const extractRole = (userResp) =>
			userResp?.data?.data?.role ?? userResp?.data?.role ?? null;

		let latestUserRole = userRole;
		while (attempts < maxAttempts) {
			const userResp = await refetchUser();
			latestUserRole = extractRole(userResp) || userRole;
			if (latestUserRole) break;
			await sleep(100);
			attempts++;
		}

		if (!latestUserRole) {
			navigate('/auth', { replace: true });
			return;
		}

		const dashboardPath = getDashboardPathForRole(latestUserRole);
		const redirectUrl = resolvePostAuthRedirectUrl({
			userRole: latestUserRole,
			portalUrlPrefix,
			dashboardPath,
			token: apiToken,
		});

		if (redirectUrl) {
			window.location.replace(redirectUrl);
			return;
		}

		navigate(dashboardPath, { replace: true });
	};

	const redirectToCorrectHostAfterLogin = (apiToken, portalUrlPrefix) => {
		const hostname = window.location.hostname;
		const currentSubdomain = getPortalSubdomain(hostname);
		const protocol = window.location.protocol;
		const port = window.location.port;

		if (portalUrlPrefix && currentSubdomain !== portalUrlPrefix) {
			const portalUrl = buildPortalUrl(
				portalUrlPrefix,
				'/auth',
				hostname,
				protocol,
				apiToken,
			);
			if (portalUrl) {
				window.location.replace(portalUrl);
				return true;
			}
		}

		if (!portalUrlPrefix && currentSubdomain) {
			const apexUrl = buildApexUrl('/auth', hostname, protocol, port, apiToken);
			window.location.replace(apexUrl);
			return true;
		}

		return false;
	};

	// Auth actions
	const login = async (userData) => {
		try {
			const response = await authApi.login(userData);
			const apiToken = response.data.token;
			const portalUrlPrefix = response.data.portalUrlPrefix ?? null;

			localStorage.setItem('token', apiToken);
			setToken(apiToken);
			touchActivity();

			if (redirectToCorrectHostAfterLogin(apiToken, portalUrlPrefix)) {
				return;
			}

			await completePostAuthNavigation(apiToken, portalUrlPrefix);
		} catch (error) {
			console.error('Login error:', error);
			throw error;
		}
	};

	const register = async (userData) => {
		try {
			const response = await authApi.register(userData);
			const apiToken = response.data.token;
			const portalUrlPrefix = response.data.portalUrlPrefix ?? null;

			localStorage.setItem('token', apiToken);
			setToken(apiToken);
			touchActivity();

			if (redirectToCorrectHostAfterLogin(apiToken, portalUrlPrefix)) {
				return;
			}

			await completePostAuthNavigation(apiToken, portalUrlPrefix);
		} catch (error) {
			console.error('Register error:', error);
			throw error;
		}
	};

	const logoutUser = () => {
		const roleForRedirect = userRole;
		const companyData = company?.data?.data || company?.data || company;
		const portalUrlPrefix = companyData?.portalUrlPrefix || null;

		clearSessionIdleState();
		requestServerLogout();
		clearAllClientAuth();
		queryClient.clear();

		if (location.pathname.startsWith('/reset-password')) {
			return;
		}

		const redirectUrl = resolveLogoutAuthUrl({
			portalUrlPrefix,
			userRole: roleForRedirect,
			hostname: window.location.hostname,
			protocol: window.location.protocol,
			port: window.location.port,
		});

		window.location.href = redirectUrl;
	};

	useEffect(() => {
		if (token) {
			localStorage.setItem(
				'authenticatedUserInfo',
				JSON.stringify({
					companyId,
					userId,
					userEmail,
				userRole,
				isCompanyOwner,
				profileImage,
				userPermissions,
				}),
			);
			// Authenticated user info saved to localStorage
		} else {
			localStorage.removeItem('authenticatedUserInfo');
		}
	}, [
		token,
		companyId,
		userId,
		userEmail,
				userRole,
				isCompanyOwner,
				profileImage,
				userPermissions,
	]);

	// Computed role booleans
	const isSystemAdmin = userRole === 'System Admin';
	const isCompanyAdmin = userRole === 'Company Admin';
	const isEmployee = userRole === 'Employee';

	return (
		<AuthenticatedUserInfoContext.Provider
			value={{
				token,
				setToken,
				user,
				loading: authLoading || userLoading,
				login,
				register,
				logout: logoutUser,
				error: userError,
				companyId,
				userId,
				userEmail,
				employeeId,
				userRole,
				isCompanyOwner,
				profileImage,
				setProfileImage,
				userPermissions,
				company: company?.data || company, // Direct company data access
				employees,
				employeesLoading,
				companyLoading,
				isSystemAdmin,
				isCompanyAdmin,
				isEmployee,
			}}>
			{children}
		</AuthenticatedUserInfoContext.Provider>
	);
};

export const useAuthenticatedUserInfo = () =>
	useContext(AuthenticatedUserInfoContext);
